/* ============================================================
   LAYAM — storytelling motion layer (dependency-free)
   Momentum smooth-scroll + scroll-reveal + stagger + parallax
   + scroll scrubbing + magnetic buttons + spotlight + counters
   + reading progress. Gated by the .la-motion class (set in
   <head> only when motion is allowed / not reduced-motion).
   ============================================================ */
(function () {
  'use strict';

  var docEl = document.documentElement;
  if (!docEl.classList.contains('la-motion')) return;

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE = window.matchMedia('(pointer: fine)').matches && !window.matchMedia('(hover: none)').matches;

  var slice = function (n) { return [].slice.call(n); };

  /* ------------------------------------------------------------
     1. MOMENTUM SMOOTH SCROLL  (desktop / fine-pointer only)
     Keeps position:sticky & fixed working by scrolling the real
     window with a lerp — no wrapper transform.
  ------------------------------------------------------------ */
  var Smooth = (function () {
    var api = { active: false, velocity: 0 };
    if (REDUCE || !FINE) return api;

    var target = window.scrollY || 0;
    var current = target;
    var running = false;
    var raf = null;

    function maxScroll() {
      return Math.max(0, docEl.scrollHeight - window.innerHeight);
    }
    function clamp(v) { return Math.max(0, Math.min(v, maxScroll())); }

    function loop() {
      var prev = current;
      current += (target - current) * 0.072;
      api.velocity = current - prev;
      if (Math.abs(target - current) < 0.35) {
        current = target;
        api.velocity = 0;
        running = false;
        window.scrollTo(0, Math.round(current));
        docEl.style.setProperty('--la-vel', '0');
        return;
      }
      window.scrollTo(0, current);
      docEl.style.setProperty('--la-vel', api.velocity.toFixed(2));
      raf = requestAnimationFrame(loop);
    }
    function start() { if (!running) { running = true; raf = requestAnimationFrame(loop); } }

    // Don't hijack wheel inside independently-scrollable overlays.
    function insideScrollable(node) {
      while (node && node !== document.body && node.nodeType === 1) {
        var cls = node.classList;
        if (cls && (cls.contains('cart-drawer') || cls.contains('menu-drawer') ||
            cls.contains('predictive-search') || cls.contains('media-modal') ||
            node.hasAttribute('data-la-native-scroll'))) return true;
        var oy = getComputedStyle(node).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 3) return true;
        node = node.parentNode;
      }
      return false;
    }

    try {
      window.addEventListener('wheel', function (e) {
        if (e.ctrlKey) return;                 // pinch-zoom
        if (insideScrollable(e.target)) return;
        e.preventDefault();
        var d = e.deltaY * (e.deltaMode === 1 ? 22 : e.deltaMode === 2 ? window.innerHeight : 1);
        target = clamp(target + d);
        start();
      }, { passive: false });

      // Re-sync when scroll comes from elsewhere (scrollbar, keys, anchors)
      window.addEventListener('scroll', function () {
        if (!running) { target = current = window.scrollY; }
      }, { passive: true });

      window.addEventListener('resize', function () { target = clamp(target); }, { passive: true });

      api.active = true;
      api.scrollTo = function (y) { target = clamp(y); start(); };
      api.resync = function () { target = current = window.scrollY; };
    } catch (err) { api.active = false; }

    return api;
  })();

  /* Smooth anchor jumps (works with or without momentum scroll) */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var href = a.getAttribute('href');
    if (!href || href === '#' || href.length < 2) return;
    var el = document.getElementById(href.slice(1));
    if (!el) return;
    e.preventDefault();
    var y = el.getBoundingClientRect().top + window.scrollY - 78;
    if (Smooth.active) { Smooth.scrollTo(y); }
    else { window.scrollTo({ top: y, behavior: 'smooth' }); }
  });

  /* ------------------------------------------------------------
     2. SCROLL REVEAL + STAGGER
  ------------------------------------------------------------ */
  var revealEls = slice(document.querySelectorAll('[data-la-reveal]'));

  slice(document.querySelectorAll('[data-la-stagger]')).forEach(function (group) {
    slice(group.querySelectorAll('[data-la-reveal]')).forEach(function (k, i) {
      k.style.setProperty('--la-order', i);
    });
  });

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('la-in'); io.unobserve(en.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    revealEls.forEach(function (el) { io.observe(el); });
  } else {
    revealEls.forEach(function (el) { el.classList.add('la-in'); });
  }
  // Failsafe: never leave content hidden
  window.addEventListener('load', function () {
    setTimeout(function () { revealEls.forEach(function (el) { el.classList.add('la-in'); }); }, 2600);
  });

  /* ------------------------------------------------------------
     3. ANIMATED COUNTERS  [data-la-count="1200"] [data-la-suffix="+"]
  ------------------------------------------------------------ */
  slice(document.querySelectorAll('[data-la-count]')).forEach(function (el) {
    var end = parseFloat(el.getAttribute('data-la-count')) || 0;
    var suffix = el.getAttribute('data-la-suffix') || '';
    var prefix = el.getAttribute('data-la-prefix') || '';
    var dur = 1600, played = false;
    function run() {
      if (played) return; played = true;
      if (REDUCE) { el.textContent = prefix + end + suffix; return; }
      var t0 = null;
      function step(ts) {
        if (!t0) t0 = ts;
        var p = Math.min((ts - t0) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        var val = Math.round(end * eased);
        el.textContent = prefix + val.toLocaleString() + suffix;
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }
    if ('IntersectionObserver' in window) {
      var co = new IntersectionObserver(function (ents) {
        ents.forEach(function (en) { if (en.isIntersecting) { run(); co.unobserve(en.target); } });
      }, { threshold: 0.6 });
      co.observe(el);
    } else { run(); }
  });

  /* ------------------------------------------------------------
     4. MAGNETIC BUTTONS  (fine-pointer only)
     Auto-applies to primary CTAs + anything [data-la-magnetic].
  ------------------------------------------------------------ */
  if (FINE && !REDUCE) {
    var magnets = slice(document.querySelectorAll('[data-la-magnetic], .layam-hero__ctas .button, .button--primary'));
    magnets.forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-la-magnetic')) || 0.4;
      el.style.willChange = 'transform';
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var mx = e.clientX - (r.left + r.width / 2);
        var my = e.clientY - (r.top + r.height / 2);
        el.style.transform = 'translate(' + (mx * strength).toFixed(1) + 'px,' + (my * strength).toFixed(1) + 'px)';
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });

    /* 5. SPOTLIGHT — cursor-tracking highlight on [data-la-spotlight] */
    slice(document.querySelectorAll('[data-la-spotlight]')).forEach(function (el) {
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        el.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
        el.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
      });
    });
  }

  /* ------------------------------------------------------------
     6. PARALLAX + SCRUB + READING PROGRESS  (single rAF loop)
     [data-la-parallax="0.15"]        translate on scroll
     [data-la-scrub]                  sets --la-progress 0→1 across viewport
  ------------------------------------------------------------ */
  var parallax = slice(document.querySelectorAll('[data-la-parallax]'));
  var scrubs = slice(document.querySelectorAll('[data-la-scrub]'));

  var bar = document.createElement('div');
  bar.className = 'la-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);

  var ticking = false;
  function frame() {
    var vh = window.innerHeight;

    if (!REDUCE) {
      parallax.forEach(function (el) {
        var r = el.getBoundingClientRect();
        var speed = parseFloat(el.getAttribute('data-la-parallax')) || 0.15;
        var offset = (r.top + r.height / 2) - vh / 2;
        el.style.transform = 'translate3d(0,' + (offset * -speed * 1.32).toFixed(1) + 'px,0)';
      });
      scrubs.forEach(function (el) {
        var r = el.getBoundingClientRect();
        // 0 when element bottom enters, 1 when its top leaves
        var p = 1 - (r.top + r.height) / (vh + r.height);
        p = Math.max(0, Math.min(1, p));
        el.style.setProperty('--la-progress', p.toFixed(4));
      });
    }

    var h = docEl.scrollHeight - vh;
    bar.style.width = (h > 0 ? (window.pageYOffset / h) * 100 : 0) + '%';
    ticking = false;
  }
  function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  frame();
})();
